import { acquireEndpoint } from "utils/SmartMerchandiserAPI";
import {
  takeEvery,
  takeLatest,
  call,
  put,
  select,
} from "typed-redux-saga/macro";
import { callApi } from "utils/SagaUtils";
import { getData, postData } from "services/ApiService";
import { getUserId, getUserName } from "services/UserService";
import { selectCurrentStoreId } from "store/store-list/StoreListSelectors";
import {
  FETCH_SEARCH_REINDEX_REQUESTS,
  SUBMIT_SEARCH_REINDEX,
  SearchReindexRequestRecord,
} from "./SearchReindexTypes";
import { searchReindexCompleted } from "./SearchReindexActions";
import { selectRunningSearchReindex } from "./SearchReindexSelectors";

// A run cannot outlive the backend's 6h limit, so older records are irrelevant.
const LOOKBACK_MS = 6 * 60 * 60 * 1000;

function* submitSearchReindex() {
  try {
    const actionType = SUBMIT_SEARCH_REINDEX;
    const constName = Object.keys({ SUBMIT_SEARCH_REINDEX })[0].toString();
    const storeId = yield* select(selectCurrentStoreId);
    const endpoint = acquireEndpoint(constName);
    const headers = {
      "x-store-id": storeId,
      "x-user-id": getUserId(),
      "x-user-name": getUserName(),
    };
    yield* call(callApi, actionType, postData, {}, endpoint, headers);
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: SUBMIT_SEARCH_REINDEX.FAILURE,
      message: e.message,
    });
  }
}

// After a websocket reconnect, settle the outcome of the reindex this browser
// is tracking if its completion message was missed while disconnected.
function* fetchSearchReindexRequests() {
  const actionType = FETCH_SEARCH_REINDEX_REQUESTS;
  try {
    const running = yield* select(selectRunningSearchReindex);
    if (!running) {
      return;
    }
    const constName = Object.keys({
      FETCH_SEARCH_REINDEX_REQUESTS,
    })[0].toString();
    const updatedSince = new Date(Date.now() - LOOKBACK_MS).toISOString();
    const endpoint = acquireEndpoint(constName, updatedSince);
    const response: any = yield* call(
      callApi,
      actionType,
      getData,
      null,
      endpoint,
      {},
      { showGenericError: false },
    );
    if (response?.type !== actionType.SUCCESS) {
      return;
    }
    const record = (
      (response.payload?.requests ?? []) as SearchReindexRequestRecord[]
    ).find((r) => r.requestId === running.requestId);
    if (!record || record.status === "IN_PROGRESS") {
      return;
    }
    yield* put(
      searchReindexCompleted({
        requestId: record.requestId,
        storeId: record.request?.headers?.["x-store-id"] ?? running.storeId,
        statusCode: record.response?.statusCode ?? 500,
        status:
          record.response?.body?.status ??
          (record.status === "SUCCEEDED" ? "done" : "failed"),
        executionId: record.response?.body?.executionId,
        reason: record.response?.body?.reason,
      }),
    );
  } catch (e: any) {
    console.error(e);
    yield* put({ type: actionType.FAILURE, showGenericError: false });
  }
}

export function* watchSubmitSearchReindex() {
  yield* takeEvery(SUBMIT_SEARCH_REINDEX.REQUEST, submitSearchReindex);
}

export function* watchFetchSearchReindexRequests() {
  yield* takeLatest(
    FETCH_SEARCH_REINDEX_REQUESTS.REQUEST,
    fetchSearchReindexRequests,
  );
}
